/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.model.Assessment
import systems.frictionless.impulse.rest.model.User

class WizardViewModel(private val restClient: RestClient) {
    private val mutableWizardStage = MutableStateFlow<WizardStage>(WizardStage.Intro(IntroSectionViewModel()))

    val wizardStage: StateFlow<WizardStage> = mutableWizardStage

    /**
     * Forces the wizard to transition to the state immediately after sign-in.  This is useful when users are
     * authenticated from outside of the wizard.
     */
    fun deepLinkToPostSignIn(authenticatedUser: AuthenticatedUser) {
        mutableWizardStage.tryEmit(WizardStage.Assessment(authenticatedUser, restClient))
    }

    /**
     * Transitions to the next stage.  Note that many stages have a "done" state in their child view models, which
     * is a requirement before transitioning.
     */
    @Suppress("ThrowsCount", "ComplexMethod", "LongMethod")
    fun next() {
        val nextStage =
            when (val wizardStateSnapshot = mutableWizardStage.value) {
                is WizardStage.Intro -> WizardStage.Login(LoginViewModel(restClient))
                is WizardStage.Login -> {
                    when (val loginStateSnapshot = wizardStateSnapshot.loginViewModel.state.value) {
                        is LoginViewModel.State.GetUser -> {
                            when (loginStateSnapshot.state.value) {
                                is NetworkViewModelState.Complete -> {
                                    val cast =
                                        loginStateSnapshot.state.value as NetworkViewModelState.Complete<Authentication.Bearer, User, AuthenticatedUser>
                                    WizardStage.Assessment(
                                        cast.result,
                                        restClient
                                    )
                                }
                                else -> error("User is not signed in")
                            }
                        }
                        else -> error("User is not signed in")
                    }
                }
                is WizardStage.Assessment -> {
                    when (val createStateSnapshot = wizardStateSnapshot.assessmentViewModel.state.value) {
                        is AssessmentViewModel.State.Done -> {
                            val user = wizardStateSnapshot.authenticatedUser
                            val assessment = createStateSnapshot.assessment

                            // Note this could be smarter about whether to put the user at the overview versus the specific
                            // question.  When the user hits next from the overview though, it will jump to the right spot.
                            // Perhaps this OK, because it gives the user context and instructions before they resume.
                            when (createStateSnapshot.state) {
                                // Complete is a special case that only gets triggered if a query string parameter was passed in
                                Assessment.State.Complete ->
                                    WizardStage.Insights(
                                        wizardStateSnapshot.authenticatedUser,
                                        assessment,
                                        restClient
                                    )
                                Assessment.State.Demographics ->
                                    WizardStage.Demographics(
                                        user,
                                        assessment,
                                        restClient
                                    )
                                Assessment.State.DemographicsOverview ->
                                    WizardStage.Demographics(
                                        user,
                                        assessment,
                                        restClient
                                    )
                                Assessment.State.Questions ->
                                    WizardStage.Questions(
                                        user,
                                        assessment,
                                        restClient
                                    )
                                Assessment.State.QuestionsOverview ->
                                    WizardStage.Questions(
                                        user,
                                        assessment,
                                        restClient
                                    )
                            }
                        }
                        else -> error("Assessment is not created")
                    }
                }
                is WizardStage.Demographics ->
                    WizardStage.Questions(
                        wizardStateSnapshot.authenticatedUser,
                        wizardStateSnapshot.assessment,
                        restClient
                    )
                is WizardStage.Questions ->
                    WizardStage.Insights(wizardStateSnapshot.authenticatedUser, wizardStateSnapshot.assessment, restClient)
                is WizardStage.Insights -> error("Cannot move past results")
            }

        mutableWizardStage.tryEmit(nextStage)
    }

    sealed class WizardStage {
        class Intro(val introSectionViewModel: IntroSectionViewModel) : WizardStage()

        class Login(val loginViewModel: LoginViewModel) : WizardStage()

        class Assessment(
            val authenticatedUser: AuthenticatedUser,
            restClient: RestClient
        ) : WizardStage() {
            val assessmentViewModel = AssessmentViewModel(authenticatedUser, restClient)
        }

        class Demographics(
            val authenticatedUser: AuthenticatedUser,
            val assessment: systems.frictionless.impulse.rest.model.Assessment,
            restClient: RestClient
        ) : WizardStage() {
            val demographicsViewModel = DemographicsViewModel(authenticatedUser, assessment, restClient)
        }

        class Questions(
            val authenticatedUser: AuthenticatedUser,
            val assessment: systems.frictionless.impulse.rest.model.Assessment,
            restClient: RestClient
        ) :
            WizardStage() {
            val questionsViewModel = QuestionsViewModel(authenticatedUser, assessment, restClient)
        }

        class Insights(
            val authenticatedUser: AuthenticatedUser,
            val assessment: systems.frictionless.impulse.rest.model.Assessment,
            val restClient: RestClient
        ) : WizardStage() {
            val resultsViewModel = InsightsViewModel(authenticatedUser, assessment, restClient)
        }
    }
}
